import { createFileRoute, Link, useLocation } from '@tanstack/react-router'
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Bell,
  CalendarClock,
  CheckCircle2,
  Clock3,
  Gauge,
  LineChart,
  Network,
  ShieldCheck,
  SlidersHorizontal,
  TrendingUp,
  Wrench,
} from 'lucide-react'
import { motion } from 'motion/react'
import { LandingPageLayout } from '@/components/landing/LandingPageLayout'
import { SectionScroller } from '@/components/landing/SectionScroller'
import { designSystem } from '@/lib/design-system'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'
import { cn } from '@/lib/utils'
import { GridSpotlight } from '@/components/ui/grid-spotlight'
import { ServiceHeroVisual } from '@/components/services/ServiceHeroVisual'

export const Route = createFileRoute('/{-$locale}/services/action-intelligence')({
  head: () => ({
    meta: [
      { title: 'Action Intelligence & Automation — Ponte Solutions' },
      {
        name: 'description',
        content:
          'Action intelligence and automation by Ponte Solutions. Turn analytics into predictive maintenance, service cases, workflow automation, and business-system integration.',
      },
      { property: 'og:title', content: 'Action Intelligence & Automation — Ponte Solutions' },
      {
        property: 'og:description',
        content:
          'Action intelligence and automation by Ponte Solutions. Turn analytics into predictive maintenance, service cases, workflow automation, and business-system integration.',
      },
    ],
  }),
  component: ActionIntelligencePage,
})

const maintenanceSignals = [
  {
    icon: CalendarClock,
    title: 'Calendar maintenance wastes visits.',
    text: 'Fixed service intervals treat healthy and stressed equipment the same, so teams replace parts too early or arrive too late.',
  },
  {
    icon: AlertTriangle,
    title: 'Failures usually leave traces.',
    text: 'Temperature drift, vibration changes, pressure instability, runtime patterns, and repeated warnings often appear before the actual failure.',
  },
  {
    icon: Bell,
    title: 'Automation must be trusted.',
    text: 'A black-box trigger is not enough. Teams need evidence, trend context, severity, and a recommended next action before workflows fire.',
  },
]

const riskSignals = [
  { label: 'Temperature Drift', value: 68, status: 'watch' },
  { label: 'Pressure Variance', value: 42, status: 'stable' },
  { label: 'Runtime Pattern', value: 81, status: 'risk' },
  { label: 'Service Window', value: 56, status: 'watch' },
]

const intelligenceSteps = [
  {
    icon: Activity,
    title: 'Baseline Behavior',
    text: 'Model normal operating ranges by asset type, site conditions, duty cycle, and seasonal pattern.',
  },
  {
    icon: TrendingUp,
    title: 'Anomaly Signals',
    text: 'Detect meaningful drift and abnormal patterns in the telemetry already produced by connected assets.',
  },
  {
    icon: Gauge,
    title: 'Risk Scoring',
    text: 'Translate signal changes into explainable risk levels, confidence, and estimated service urgency.',
  },
  {
    icon: Wrench,
    title: 'Automated Action',
    text: 'Route evidence-backed recommendations into maintenance planning, service cases, CMMS, CRM, ERP, or field workflows.',
  },
]

const deliverySteps = [
  {
    eyebrow: '01',
    title: 'Signal Audit',
    text: 'We inspect historical and live telemetry, identify useful failure indicators, and separate reliable signals from noisy data.',
  },
  {
    eyebrow: '02',
    title: 'Early Warning Pilot',
    text: 'We build a first anomaly and risk-scoring loop with clear evidence, thresholds, and technician feedback capture.',
  },
  {
    eyebrow: '03',
    title: 'Action Integration',
    text: 'We connect recommendations to maintenance workflows, service cases, business systems, and feedback loops as field outcomes return.',
  },
]

const outcomes = [
  'Earlier warning before failures',
  'Fewer unnecessary preventive visits',
  'Evidence-backed service decisions',
  'Feedback loop for continuous tuning',
]

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
}

function onSectionMouseMove(e: React.MouseEvent<HTMLElement>) {
  const rect = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--spot-x', `${e.clientX - rect.left}px`)
  e.currentTarget.style.setProperty('--spot-y', `${e.clientY - rect.top}px`)
}

function onCardMouseMove(e: React.MouseEvent<HTMLElement>) {
  const rect = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`)
  e.currentTarget.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`)
}

function CardSpotlight() {
  return (
    <>
      <div className="pointer-events-none absolute inset-0 z-0 rounded-xl opacity-0 backdrop-blur-[1px] transition-opacity duration-200 group-hover:opacity-100" />
      <div className="pointer-events-none absolute inset-0 z-0 rounded-xl bg-accent/[0.08] opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
      <div
        className="pointer-events-none absolute inset-0 z-0 rounded-xl opacity-0 transition-opacity duration-200 group-hover:opacity-100"
        style={{
          background:
            'radial-gradient(240px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), oklch(68% .095 52 / 0.2), transparent 70%)',
        }}
      />
    </>
  )
}

function ActionIntelligencePage() {
  return (
    <LandingPageLayout>
      <div className="ponte-service-page">
        <SectionScroller labels={['Action', 'Signals', 'Intelligence', 'Automation', 'Start']}>
          <ActionIntelligenceHeroSection />
          <MaintenanceTrapSection />
          <RiskIntelligenceSection />
          <ServiceWorkflowSection />
          <ActionIntelligenceCtaSection />
        </SectionScroller>
      </div>
    </LandingPageLayout>
  )
}

function ActionIntelligenceHeroSection() {
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)

  return (
    <section
      className="ponte-service-section ponte-service-section--hero relative isolate flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-10 text-white sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight />
      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-10 lg:grid-cols-[0.9fr_1.1fr]">
        <motion.div
          className="max-w-3xl py-10"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <motion.div
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-3 py-1.5 text-xs font-medium text-white/75"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
          >
            <TrendingUp className="h-3.5 w-3.5 text-accent" />
            Action Intelligence & Automation
          </motion.div>

          <motion.h1
            className={cn(
              designSystem.typography.heading.h1,
              'max-w-4xl text-balance text-[clamp(2.8rem,7vw,5.5rem)] font-semibold leading-[0.92] tracking-normal text-white',
            )}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.2, ease: 'easeOut' }}
          >
            Turn analytics into{' '}
            <span className="text-accent">automated action.</span>
          </motion.h1>

          <motion.p
            className="mt-7 max-w-2xl text-pretty text-lg leading-8 text-white/72 md:text-xl"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Ponte connects your analytics layer to action: predictive maintenance, service cases,
            workflow automation, business-system integration, and explainable recommendations your
            team can trust.
          </motion.p>

          <motion.div
            className="mt-9 flex flex-col gap-3 sm:flex-row"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.4 }}
          >
            <Link
              to={withLocalePath('/contact', locale)}
              className="landing-cta-primary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold"
            >
              Discuss Automation
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={withLocalePath('/services/data-processing-analytics', locale)}
              className="landing-cta-secondary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold"
            >
              Start with Analytics
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </motion.div>

        <ServiceHeroVisual service="action" />
      </div>
    </section>
  )
}

function MaintenanceTrapSection() {
  return (
    <section className="ponte-service-section ponte-service-section--deep relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 sm:px-6 lg:px-8">
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_20%_60%,rgba(214,151,101,.1),transparent_40%)]"
      />

      <motion.div
        className="relative z-10 mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <motion.div variants={itemVariants}>
          <span className={cn(designSystem.typography.display.eyebrow, 'mb-3 block dark:font-mono dark:text-primary/70')}>
            Reactive Maintenance Trap
          </span>
          <h2
            className={cn(
              designSystem.typography.heading.h1,
              'max-w-xl text-balance text-3xl font-semibold tracking-normal text-white sm:text-4xl lg:text-5xl',
            )}
          >
            Service should follow{' '}
            <span className="text-accent">asset condition</span>, not only the calendar.
          </h2>
          <p className="mt-5 max-w-xl text-base leading-8 text-white/60">
            Action Intelligence & Automation turns monitored telemetry into operational action:
            which asset is drifting, why it matters, what confidence exists, and which workflow
            should run next.
          </p>
        </motion.div>

        <motion.div className="grid gap-4" variants={containerVariants}>
          {maintenanceSignals.map((item) => (
            <motion.div
              key={item.title}
              variants={itemVariants}
              className="group relative overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.03] p-5 backdrop-blur-sm transition-all duration-300 hover:border-accent/40"
              style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
              onMouseMove={onCardMouseMove}
            >
              <CardSpotlight />
              <div className="relative z-10 grid gap-4 sm:grid-cols-[3rem_1fr]">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-accent/10 text-accent transition-colors duration-300 group-hover:bg-accent group-hover:text-accent-foreground">
                  <item.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-white/55">{item.text}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  )
}

function RiskIntelligenceSection() {
  return (
    <section
      className="ponte-service-section ponte-service-section--mesh relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 text-white sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight />
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,rgba(214,151,101,.16),transparent_36%)]"
      />

      <motion.div
        className="relative z-10 mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <motion.div variants={itemVariants}>
          <span className={cn(designSystem.typography.display.eyebrow, 'mb-3 block dark:font-mono dark:text-primary/70')}>
            Signal Intelligence
          </span>
          <h2
            className={cn(
              designSystem.typography.heading.h1,
              'max-w-xl text-balance text-3xl font-semibold tracking-normal text-white sm:text-4xl lg:text-5xl',
            )}
          >
            Automation needs{' '}
            <span className="text-accent">evidence operators can inspect.</span>
          </h2>
          <p className="mt-5 max-w-xl text-base leading-8 text-white/55">
            We build explainable early-warning views: baseline, drift, risk score, confidence,
            recommended action, workflow target, and the historical signal that triggered the warning.
          </p>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="rounded-2xl border border-white/[0.08] bg-white/[0.035] p-4 shadow-2xl shadow-black/20 backdrop-blur-sm"
        >
          <div className="rounded-xl border border-border/70 bg-card/95 p-4 shadow-sm dark:border-white/[0.08] dark:bg-[#0a1116] dark:shadow-none">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-accent" />
                <span className="text-sm font-semibold text-white">Pump A-17 Risk Profile</span>
              </div>
              <span className="rounded-full border border-accent/30 bg-accent/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">
                Watch
              </span>
            </div>

            <div className="grid gap-3 sm:grid-cols-[0.85fr_1.15fr]">
              <div className="rounded-lg border border-white/[0.07] bg-white/[0.03] p-4">
                <p className="text-[11px] uppercase tracking-[0.12em] text-white/42">Health Score</p>
                <div className="mt-5 flex items-end gap-3">
                  <span className="text-6xl font-semibold text-accent">72</span>
                  <span className="pb-2 text-sm text-white/45">/100</span>
                </div>
                <p className="mt-4 text-sm leading-6 text-white/55">
                  Elevated runtime variance and temperature drift detected over the last service window.
                </p>
              </div>

              <div className="rounded-lg border border-white/[0.07] bg-white/[0.03] p-4">
                <div className="mb-4 flex items-center justify-between">
                  <span className="text-xs font-semibold text-white/70">Leading Signals</span>
                  <LineChart className="h-4 w-4 text-accent" />
                </div>
                <div className="space-y-4">
                  {riskSignals.map((signal) => (
                    <div key={signal.label}>
                      <div className="mb-1.5 flex justify-between text-xs text-white/55">
                        <span>{signal.label}</span>
                        <span className="capitalize">{signal.status}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-white/8">
                        <div className="h-full rounded-full bg-accent" style={{ width: `${signal.value}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-3 rounded-lg border border-accent/20 bg-accent/10 p-4">
              <div className="flex items-start gap-3">
                <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <p className="text-sm leading-6 text-white/72">
                  Recommended action: inspect bearing assembly during next planned visit. Escalate if
                  temperature drift continues for another 48 hours.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  )
}

function ServiceWorkflowSection() {
  return (
    <section className="ponte-service-section ponte-service-section--deep relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 text-white sm:px-6 lg:px-8">
      <motion.div
        className="relative z-10 mx-auto w-full max-w-7xl"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <motion.div className="mx-auto max-w-3xl text-center" variants={itemVariants}>
          <span className={cn(designSystem.typography.display.eyebrow, 'mb-3 block dark:font-mono dark:text-primary/70')}>
            Maintenance Intelligence Loop
          </span>
          <h2
            className={cn(
              designSystem.typography.heading.h1,
              'text-balance text-3xl font-semibold tracking-normal text-white sm:text-4xl lg:text-5xl',
            )}
          >
            Intelligence only creates value when it reaches{' '}
            <span className="text-accent">the action workflow.</span>
          </h2>
          <p className="mt-5 text-base leading-8 text-white/55">
            The goal is not a model demo. The goal is a repeatable loop from telemetry to
            recommendation, automated routing, human action, outcome capture, and better future
            decisions.
          </p>
        </motion.div>

        <div className="mt-12 grid gap-4 lg:grid-cols-4">
          {intelligenceSteps.map((step, index) => (
            <motion.div
              key={step.title}
              variants={itemVariants}
              className="group relative overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.025] p-5 backdrop-blur-sm transition-all duration-300 hover:border-accent/30"
              style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
              onMouseMove={onCardMouseMove}
            >
              {index < intelligenceSteps.length - 1 && (
                <div className="pointer-events-none absolute -right-2 top-[2.75rem] z-20 hidden h-[2px] w-4 bg-gradient-to-r from-accent/40 to-transparent lg:block" />
              )}
              <CardSpotlight />
              <div className="relative z-10">
                <div className="mb-8 flex items-center justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-md bg-accent text-accent-foreground transition-transform duration-300 group-hover:scale-105">
                    <step.icon className="h-5 w-5" />
                  </div>
                  <span className="font-mono text-xs text-white/30">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-white transition-colors duration-300 group-hover:text-accent">
                  {step.title}
                </h3>
                <p className="mt-3 text-sm leading-7 text-white/50">{step.text}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div className="mt-8 grid gap-4 lg:grid-cols-3" variants={containerVariants}>
          {deliverySteps.map((step) => (
            <motion.div
              key={step.title}
              variants={itemVariants}
              className="group relative overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.025] p-5 backdrop-blur-sm transition-all duration-300 hover:border-accent/25"
              style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
              onMouseMove={onCardMouseMove}
            >
              <CardSpotlight />
              <div className="relative z-10">
                <p className="font-mono text-xs font-semibold text-accent">{step.eyebrow}</p>
                <h3 className="mt-4 text-lg font-semibold text-white">{step.title}</h3>
                <p className="mt-2 text-sm leading-7 text-white/50">{step.text}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  )
}

function ActionIntelligenceCtaSection() {
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)

  return (
    <section
      className="ponte-service-section ponte-service-section--cta relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 text-white sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight gridSize={88} baseOpacity={0.12} highlightOpacity={0.22} />
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(214,151,101,.14),transparent_45%)]"
      />

      <motion.div
        className="relative z-10 mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <motion.div variants={itemVariants}>
          <span className={cn(designSystem.typography.display.eyebrow, 'mb-3 block dark:font-mono dark:text-primary/70')}>
            What Action Intelligence Unlocks
          </span>
          <h2
            className={cn(
              designSystem.typography.heading.h1,
              'max-w-2xl text-balance text-3xl font-semibold tracking-normal text-white sm:text-4xl lg:text-5xl',
            )}
          >
            When insight triggers the right workflow,{' '}
            <span className="text-accent">service becomes proactive.</span>
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-8 text-white/55">
            Action Intelligence & Automation turns connected-product data into a repeatable
            operating layer: detect, explain, recommend, route, automate, and learn from every
            service outcome.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={withLocalePath('/contact', locale)}
              className="landing-cta-primary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold"
            >
              Start an Automation Project
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={withLocalePath('/services/connect', locale)}
              className="landing-cta-secondary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold"
            >
              Review Connect
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="group relative overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.025] p-6 backdrop-blur-sm transition-all duration-300 hover:border-accent/25"
          style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
          onMouseMove={onCardMouseMove}
        >
          <CardSpotlight />
          <div className="relative z-10">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-md bg-accent text-accent-foreground">
                <Wrench className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Action outcomes</h3>
                <p className="text-sm text-white/50">Automation and service intelligence your team can operationalize.</p>
              </div>
            </div>

            <div className="grid gap-3">
              {outcomes.map((outcome) => (
                <div
                  key={outcome}
                  className="flex items-start gap-3 rounded-lg bg-white/[0.04] p-3 transition-colors duration-200 hover:bg-white/[0.07]"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <span className="text-sm font-medium text-white/85">{outcome}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-3 gap-2 text-center">
              {[
                { icon: Activity, label: 'Signals' },
                { icon: SlidersHorizontal, label: 'Rules' },
                { icon: Network, label: 'Routing' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg border border-white/[0.06] bg-white/[0.03] p-3 transition-all duration-200 hover:border-accent/20 hover:bg-white/[0.05]"
                >
                  <item.icon className="mx-auto h-4 w-4 text-accent" />
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/50">
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  )
}
